import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { scrollToId, scrollToTop } from "../lib/scroll";
import { EASE } from "./Reveal";

export default function Header() {
  const { t, lang, setLang, languages } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    if (isHome) scrollToId(id);
    else {
      navigate("/");
      setTimeout(() => scrollToId(id), 420);
    }
  };

  const navItems = [
    ["experiencias", t.nav.experiencias],
    ["la-cocina", t.nav.cocina],
    ["edouard", t.nav.edouard],
    ["galeria", t.nav.galeria],
    ["opiniones", t.nav.opiniones],
    ["contacto", t.nav.contacto],
  ];

  const solid = scrolled || !isHome || open;

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid
            ? "border-b border-white/10 bg-charcoal/90 py-3 backdrop-blur-md"
            : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-10">
          <button
            data-testid="header-logo"
            onClick={() => {
              setOpen(false);
              if (!isHome) navigate("/");
              else scrollToTop();
            }}
            className="text-left leading-none"
          >
            <span className="font-serif text-xl text-cream">Wild Spoon</span>
            <span className="mt-1 block text-[9px] uppercase tracking-[0.34em] text-cream/50">
              Edu Vincent · Granada
            </span>
          </button>

          <nav className="hidden items-center gap-7 lg:flex">
            {navItems.map(([id, label]) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className="text-[13px] text-cream/80 transition-colors duration-300 hover:text-cream"
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div className="hidden items-center gap-1 sm:flex">
              {languages.map(([code, label]) => (
                <button
                  key={code}
                  data-testid={`language-switcher-${code}`}
                  onClick={() => setLang(code)}
                  className={`px-1.5 text-[11px] tracking-widest transition-colors ${
                    lang === code ? "text-terra" : "text-cream/50 hover:text-cream"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              data-testid="header-cta-reservar"
              onClick={() => {
                setOpen(false);
                navigate("/reservas");
              }}
              className="hidden rounded-full bg-terra px-5 py-2 text-[13px] font-medium text-cream transition-colors duration-300 hover:bg-terra-dark sm:block"
            >
              {t.nav.reservar}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="text-cream lg:hidden"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-charcoal px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-3">
              {navItems.map(([id, label], i) => (
                <motion.button
                  key={id}
                  data-testid={`mobile-nav-${id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.07 * i, duration: 0.5, ease: EASE }}
                  onClick={() => go(id)}
                  className="text-left font-serif text-4xl text-cream transition-colors hover:text-terra"
                >
                  {label}
                </motion.button>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-12 flex items-center justify-between"
            >
              <div className="flex gap-3">
                {languages.map(([code, label]) => (
                  <button
                    key={code}
                    data-testid={`mobile-language-${code}`}
                    onClick={() => setLang(code)}
                    className={`text-xs tracking-widest ${
                      lang === code ? "text-terra" : "text-cream/50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                data-testid="mobile-cta-reservar"
                onClick={() => {
                  setOpen(false);
                  navigate("/reservas");
                }}
                className="rounded-full bg-terra px-7 py-3 text-sm font-medium text-cream"
              >
                {t.nav.reservar}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
